import type { ReactNode } from 'react';

/**
 * UI strings. `en` is the source; every other language must match its shape (checked by tsc).
 * Data labels (regions, indicators, income groups, ownership) come from the API and stay as-is.
 * Titles are JSX so each language can place the gold <em> where its word order wants it.
 */
const en = {
  language: 'Language',
  primaryNav: 'Primary',
  navOverview: 'Overview',
  navTrends: 'Trends',
  navBenchmarking: 'Benchmarking',
  navMap: 'Africa map',
  theme: 'Theme',
  themeLight: 'Light',
  themeDark: 'Dark',
  themeSystem: 'System',
  signOut: 'Sign out',
  filtersButton: 'Filters',

  noAccessTitle: 'No access',
  noAccessBody: (role: ReactNode) => <>Your account is signed in but does not have the {role} role.</>,
  unavailableTitle: 'Sign-in unavailable',
  unavailableBody: 'The identity provider could not be reached. Try again in a moment.',
  retry: 'Retry',

  noData: 'No data for the current filters.',
  requestFailed: 'Request failed',

  flRegions: 'Regions',
  flIncome: 'Income',
  flOwnership: 'Ownership',
  none: 'none',
  all: 'all',

  filters: 'Filters',
  reset: 'Reset',
  indicator: 'Indicator',
  country: 'Country',
  year: 'Year',
  yearRange: 'Year range',
  region: 'Region',
  incomeGroup: 'Income group',
  ownership: 'Ownership',
  mockNote: 'Mock dataset · values are illustrative only.',
  rangeFrom: 'from',
  rangeTo: 'to',
  rangeStart: (label: string) => `${label} start`,
  rangeEnd: (label: string) => `${label} end`,

  overviewTitle: <>Performance <em>overview</em></>,
  kpiUtilities: 'Utilities',
  kpiCountries: 'Countries',
  kpiMedian: 'Median value',
  kpiObservations: 'Observations',
  inSelection: 'in the selection',
  noRefYear: 'no reference year',
  utilityYears: 'utility-years',
  refYear: (y: number) => `reference year ${y}`,
  medianByRegion: <>Median by <em>region</em></>,
  distribution: <em>Distribution</em>,
  outliers: (n: number) => (n === 1 ? '1 outlier outside the shown range' : `${n} outliers outside the shown range`),
  nUtilities: (n: string) => `${n} utilities`,
  medianIn: (y: number | null) => `Median (${y ?? '—'})`,

  trendsTitle: <>Performance <em>over time</em></>,
  trendNote: 'one region highlighted, hover or click a legend entry to switch',
  clickToPin: 'click to pin',
  regions: 'Regions',
  trendAria: 'Median indicator value over time, by region',

  benchTitle: <>Utility <em>benchmarking</em></>,
  mapTitle: <>Africa <em>regional map</em></>,
  mapModeExplorer: 'Explorer',
  mapModeCompare: 'Compare Countries',
  mapModeProfile: 'Country Profile',
  compareMaxLimit: 'Maximum 4 countries can be compared at once',
  compareSelectPrompt: 'Click countries on the map to add them to the comparison (up to 4).',
  compareNoSelection: 'No countries selected yet',
  compareAddCountry: 'Add country',
  compareMediansVsAfrica: 'National medians vs African median',
  africaBenchmark: 'African median',
  betterThanBenchmark: 'Above African median',
  worseThanBenchmark: 'Below African median',
  badgeBetter: 'better',
  badgeBelow: 'below',
  filtersActive: 'Filters active',
  mapAria: 'Map of Africa',
  selectedInComparison: ' [Selected in comparison]',
  radarAria: 'Multidimensional Radar Chart',
  clearAll: 'Clear all',
  removeCountry: (c: string) => `Remove ${c}`,
  utilityBreakdown: 'Utility Performance Breakdown',
  nationalMedian: 'National Median',
  spread: 'Spread (Δ)',
  oneObservation: '1 obs.',
  noDataCard: 'No data',
  noObservationsForYear: 'No utility observations for this indicator in selected year.',
  memberUtilities: 'Member Utilities',
  noReportingUtilities: 'No reporting utilities',
  radarAfrMedian: 'AFR. MEDIAN (P50)',
  categories: 'Categories',
  components: 'Components',
  formula: 'Formula',
  score: 'Score',
  radarDiagTitle: <>Multidimensional <em>performance diagnostic</em></>,
  radarDiagSubtitle: 'Cross-indicator radar normalized against the continental frontier (hover vertices for details)',
  radarFooterNote: (
    <>Outer boundary = <span className="text-gold font-semibold">100 Frontier Score</span> (inverted indicators normalized) · Hover vertices for definitions & formulas</>
  ),
  profileSelectPrompt: 'Click a country on the map to view its multi-indicator profile.',
  profileNoCountry: 'Select a country',
  profileTitle: (country: string) => <>Country profile: <em>{country}</em></>,
  profileRadarSubtitle: 'Multi-indicator diagnostic normalized against continental frontier',
  profileIndicatorsTable: 'Indicator Breakdown',
  profileExcellenceScore: 'Frontier Score',
  profileAfricaMedian: 'Africa Median',
  profileRawValue: 'Raw Value',
  mapWorse: (inverted: boolean): string => (inverted ? 'Higher (worse)' : 'Lower (worse)'),
  mapBetter: (inverted: boolean): string => (inverted ? 'Lower (better)' : 'Higher (better)'),
  mapProjectionNote: 'Equal Earth projection · Modern equal-area standard preserving Africa’s true proportions and relative land area without distortion.',
  colRank: 'Rank',
  colUtility: 'Utility',
  colCountry: 'Country',
  colRegion: 'Region',
  colOwnership: 'Ownership',
  colType: 'Type',
  colValue: 'Value',
  rankedBy: (indicator: string, y: number, lowerIsBetter: boolean) => (
    <>Ranked by <b>{indicator}</b> ({y}) · <b>{lowerIsBetter ? 'lower' : 'higher'}</b> is better</>
  ),
  rankingCaption: (n: number, y: number | null) => `${n} utilities reported this indicator in ${y ?? '—'}`,

  search: 'Search utility or country',
  searchAria: 'Search table',
  noRows: 'No rows match.',
  rowsOf: (first: number, last: number, total: string) => `${first}–${last} of ${total}`,
  prev: 'Previous',
  next: 'Next',
  page: (i: number, n: number) => `Page ${i} / ${n}`,

  utilitiesWord: 'utilities',
  binRange: (a: string, b: string) => `${a} to ${b}`,
  distAria: 'Distribution of utility values',
  barsAria: (label: string) => `${label} by region`,
  barTip: (label: string, n: string) => `${label} · ${n} utilities`,

  units: { hoursYear: 'h/yr', interruptions: 'int.', days: 'days' },
};

export type Messages = typeof en;

const fr: Messages = {
  language: 'Langue',
  primaryNav: 'Navigation principale',
  navOverview: "Vue d'ensemble",
  navTrends: 'Tendances',
  navBenchmarking: 'Comparaison',
  navMap: "Carte d'Afrique",
  theme: 'Thème',
  themeLight: 'Clair',
  themeDark: 'Sombre',
  themeSystem: 'Système',
  signOut: 'Se déconnecter',
  filtersButton: 'Filtres',

  noAccessTitle: 'Accès refusé',
  noAccessBody: (role) => <>Votre compte est connecté mais ne dispose pas du rôle {role}.</>,
  unavailableTitle: 'Connexion indisponible',
  unavailableBody: "Le fournisseur d'identité est injoignable. Réessayez dans un instant.",
  retry: 'Réessayer',

  noData: 'Aucune donnée pour les filtres actuels.',
  requestFailed: 'Échec de la requête',

  flRegions: 'Régions',
  flIncome: 'Revenu',
  flOwnership: 'Propriété',
  none: 'aucun',
  all: 'tous',

  filters: 'Filtres',
  reset: 'Réinitialiser',
  indicator: 'Indicateur',
  country: 'Pays',
  year: 'Année',
  yearRange: 'Période',
  region: 'Région',
  incomeGroup: 'Groupe de revenu',
  ownership: 'Propriété',
  mockNote: 'Jeu de données fictif · valeurs purement indicatives.',
  rangeFrom: 'début',
  rangeTo: 'fin',
  rangeStart: (label) => `${label} : début`,
  rangeEnd: (label) => `${label} : fin`,

  overviewTitle: <>Vue d'ensemble <em>des performances</em></>,
  kpiUtilities: 'Opérateurs',
  kpiCountries: 'Pays',
  kpiMedian: 'Valeur médiane',
  kpiObservations: 'Observations',
  inSelection: 'dans la sélection',
  noRefYear: "pas d'année de référence",
  utilityYears: 'opérateurs-années',
  refYear: (y) => `année de référence ${y}`,
  medianByRegion: <>Médiane par <em>région</em></>,
  distribution: <em>Distribution</em>,
  outliers: (n) => (n === 1 ? '1 valeur extrême hors de la plage affichée' : `${n} valeurs extrêmes hors de la plage affichée`),
  nUtilities: (n) => `${n} opérateurs`,
  medianIn: (y) => `Médiane (${y ?? '—'})`,

  trendsTitle: <>Performances <em>dans le temps</em></>,
  trendNote: 'une région mise en avant ; survolez ou cliquez sur la légende pour changer',
  clickToPin: 'cliquer pour épingler',
  regions: 'Régions',
  trendAria: "Valeur médiane de l'indicateur dans le temps, par région",

  benchTitle: <>Comparaison <em>des opérateurs</em></>,
  mapTitle: <>Carte <em>d'Afrique</em></>,
  mapModeExplorer: 'Explorateur',
  mapModeCompare: 'Comparer les pays',
  mapModeProfile: 'Profil du pays',
  compareMaxLimit: 'Un maximum de 4 pays peut être comparé simultanément',
  compareSelectPrompt: 'Cliquez sur les pays sur la carte pour les ajouter à la comparaison (jusqu’à 4).',
  compareNoSelection: 'Aucun pays sélectionné pour le moment',
  compareAddCountry: 'Ajouter un pays',
  compareMediansVsAfrica: 'Médianes nationales vs médiane africaine',
  africaBenchmark: 'Médiane Afrique',
  betterThanBenchmark: 'Supérieur à la médiane africaine',
  worseThanBenchmark: 'Inférieur à la médiane africaine',
  badgeBetter: 'favorable',
  badgeBelow: 'en deçà',
  filtersActive: 'Filtres actifs',
  mapAria: "Carte de l'Afrique",
  selectedInComparison: ' [Sélectionné pour comparaison]',
  radarAria: 'Graphique radar multidimensionnel',
  clearAll: 'Tout effacer',
  removeCountry: (c: string) => `Supprimer ${c}`,
  utilityBreakdown: 'Détail des performances des services',
  nationalMedian: 'Médiane nationale',
  spread: 'Écart (Δ)',
  oneObservation: '1 obs.',
  noDataCard: 'Aucune donnée',
  noObservationsForYear: 'Aucune observation pour cet indicateur pour l’année sélectionnée.',
  memberUtilities: 'Services membres',
  noReportingUtilities: 'Aucun service déclarant',
  radarAfrMedian: 'MÉDIANE AFR. (P50)',
  categories: 'Catégories',
  components: 'Composantes',
  formula: 'Formule',
  score: 'Score',
  radarDiagTitle: <>Diagnostic <em>multidimensionnel de performance</em></>,
  radarDiagSubtitle: 'Radar multi-indicateurs normalisé par rapport à la frontière continentale (survolez les sommets pour les détails)',
  radarFooterNote: (
    <>Limite extérieure = <span className="text-gold font-semibold">Score de frontière 100</span> (indicateurs inversés normalisés) · Survolez les sommets pour les définitions et formules</>
  ),
  profileSelectPrompt: 'Cliquez sur un pays sur la carte pour consulter son profil multi-indicateurs.',
  profileNoCountry: 'Sélectionner un pays',
  profileTitle: (country) => <>Profil du pays : <em>{country}</em></>,
  profileRadarSubtitle: 'Diagnostic multi-indicateurs normalisé par rapport à la frontière continentale',
  profileIndicatorsTable: 'Détail des indicateurs',
  profileExcellenceScore: 'Score de frontière',
  profileAfricaMedian: 'Médiane Afrique',
  profileRawValue: 'Valeur brute',
  mapWorse: (inverted) => (inverted ? 'Plus élevé (défavorable)' : 'Plus bas (défavorable)'),
  mapBetter: (inverted) => (inverted ? 'Plus bas (favorable)' : 'Plus élevé (favorable)'),
  mapProjectionNote: "Projection Equal Earth · Standard moderne à surfaces équivalentes préservant les proportions et la superficie réelles de l'Afrique sans distorsion.",
  colRank: 'Rang',
  colUtility: 'Opérateur',
  colCountry: 'Pays',
  colRegion: 'Région',
  colOwnership: 'Propriété',
  colType: 'Type',
  colValue: 'Valeur',
  rankedBy: (indicator, y, lowerIsBetter) => (
    <>Classé par <b>{indicator}</b> ({y}) · le plus <b>{lowerIsBetter ? 'bas' : 'élevé'}</b> est le meilleur</>
  ),
  rankingCaption: (n, y) => `${n} opérateurs ont déclaré cet indicateur en ${y ?? '—'}`,

  search: 'Rechercher un opérateur ou un pays',
  searchAria: 'Rechercher dans le tableau',
  noRows: 'Aucune ligne ne correspond.',
  rowsOf: (first, last, total) => `${first}–${last} sur ${total}`,
  prev: 'Précédent',
  next: 'Suivant',
  page: (i, n) => `Page ${i} / ${n}`,

  utilitiesWord: 'opérateurs',
  binRange: (a, b) => `${a} à ${b}`,
  distAria: 'Distribution des valeurs des opérateurs',
  barsAria: (label) => `${label} par région`,
  barTip: (label, n) => `${label} · ${n} opérateurs`,

  units: { hoursYear: 'h/an', interruptions: 'int.', days: 'jours' },
};

const pt: Messages = {
  language: 'Idioma',
  primaryNav: 'Navegação principal',
  navOverview: 'Visão geral',
  navTrends: 'Tendências',
  navBenchmarking: 'Comparação',
  navMap: 'Mapa de África',
  theme: 'Tema',
  themeLight: 'Claro',
  themeDark: 'Escuro',
  themeSystem: 'Sistema',
  signOut: 'Terminar sessão',
  filtersButton: 'Filtros',

  noAccessTitle: 'Sem acesso',
  noAccessBody: (role) => <>A sua conta tem sessão iniciada, mas não tem a função {role}.</>,
  unavailableTitle: 'Início de sessão indisponível',
  unavailableBody: 'Não foi possível contactar o fornecedor de identidade. Tente novamente dentro de momentos.',
  retry: 'Tentar novamente',

  noData: 'Sem dados para os filtros atuais.',
  requestFailed: 'O pedido falhou',

  flRegions: 'Regiões',
  flIncome: 'Rendimento',
  flOwnership: 'Propriedade',
  none: 'nenhum',
  all: 'todos',

  filters: 'Filtros',
  reset: 'Repor',
  indicator: 'Indicador',
  country: 'País',
  year: 'Ano',
  yearRange: 'Período',
  region: 'Região',
  incomeGroup: 'Grupo de rendimento',
  ownership: 'Propriedade',
  mockNote: 'Dados fictícios · valores apenas ilustrativos.',
  rangeFrom: 'início',
  rangeTo: 'fim',
  rangeStart: (label) => `${label}: início`,
  rangeEnd: (label) => `${label}: fim`,

  overviewTitle: <>Visão geral <em>do desempenho</em></>,
  kpiUtilities: 'Operadoras',
  kpiCountries: 'Países',
  kpiMedian: 'Valor mediano',
  kpiObservations: 'Observações',
  inSelection: 'na seleção',
  noRefYear: 'sem ano de referência',
  utilityYears: 'operadora-anos',
  refYear: (y) => `ano de referência ${y}`,
  medianByRegion: <>Mediana por <em>região</em></>,
  distribution: <em>Distribuição</em>,
  outliers: (n) => (n === 1 ? '1 valor atípico fora do intervalo mostrado' : `${n} valores atípicos fora do intervalo mostrado`),
  nUtilities: (n) => `${n} operadoras`,
  medianIn: (y) => `Mediana (${y ?? '—'})`,

  trendsTitle: <>Desempenho <em>ao longo do tempo</em></>,
  trendNote: 'uma região em destaque; passe o cursor ou clique na legenda para mudar',
  clickToPin: 'clique para fixar',
  regions: 'Regiões',
  trendAria: 'Valor mediano do indicador ao longo do tempo, por região',

  benchTitle: <>Comparação <em>de operadoras</em></>,
  mapTitle: <>Mapa <em>de África</em></>,
  mapModeExplorer: 'Explorador',
  mapModeCompare: 'Comparar países',
  mapModeProfile: 'Perfil do país',
  compareMaxLimit: 'É possível comparar no máximo 4 países em simultâneo',
  compareSelectPrompt: 'Clique nos países no mapa para os adicionar à comparação (até 4).',
  compareNoSelection: 'Nenhum país selecionado ainda',
  compareAddCountry: 'Adicionar país',
  compareMediansVsAfrica: 'Medianas nacionais vs mediana africana',
  africaBenchmark: 'Mediana África',
  betterThanBenchmark: 'Acima da mediana africana',
  worseThanBenchmark: 'Abaixo da mediana africana',
  badgeBetter: 'melhor',
  badgeBelow: 'abaixo',
  filtersActive: 'Filtros ativos',
  mapAria: 'Mapa da África',
  selectedInComparison: ' [Selecionado para comparação]',
  radarAria: 'Gráfico de radar multidimensional',
  clearAll: 'Limpar tudo',
  removeCountry: (c: string) => `Remover ${c}`,
  utilityBreakdown: 'Detalhamento do desempenho dos serviços',
  nationalMedian: 'Mediana nacional',
  spread: 'Dispersão (Δ)',
  oneObservation: '1 obs.',
  noDataCard: 'Sem dados',
  noObservationsForYear: 'Nenhuma observação para este indicador no ano selecionado.',
  memberUtilities: 'Serviços membros',
  noReportingUtilities: 'Nenhum serviço declarante',
  radarAfrMedian: 'MEDIANA AFR. (P50)',
  categories: 'Categorias',
  components: 'Componentes',
  formula: 'Fórmula',
  score: 'Pontuação',
  radarDiagTitle: <>Diagnóstico <em>multidimensional de desempenho</em></>,
  radarDiagSubtitle: 'Radar multi-indicadores normalizado em relação à fronteira continental (passe o cursor sobre os vértices para detalhes)',
  radarFooterNote: (
    <>Limite externo = <span className="text-gold font-semibold">Pontuação de fronteira 100</span> (indicadores invertidos normalizados) · Passe o cursor sobre os vértices para definições e fórmulas</>
  ),
  profileSelectPrompt: 'Clique num país no mapa para ver o seu perfil multi-indicadores.',
  profileNoCountry: 'Selecionar um país',
  profileTitle: (country) => <>Perfil do país: <em>{country}</em></>,
  profileRadarSubtitle: 'Diagnóstico multi-indicadores normalizado em relação à fronteira continental',
  profileIndicatorsTable: 'Detalhamento de indicadores',
  profileExcellenceScore: 'Pontuação de fronteira',
  profileAfricaMedian: 'Mediana de África',
  profileRawValue: 'Valor bruto',
  mapWorse: (inverted) => (inverted ? 'Mais alto (pior)' : 'Mais baixo (pior)'),
  mapBetter: (inverted) => (inverted ? 'Mais baixo (melhor)' : 'Mais alto (melhor)'),
  mapProjectionNote: 'Projeção Equal Earth · Padrão moderno de áreas equivalentes que preserva as proporções e a dimensão real de África sem distorções.',
  colRank: 'Posição',
  colUtility: 'Operadora',
  colCountry: 'País',
  colRegion: 'Região',
  colOwnership: 'Propriedade',
  colType: 'Tipo',
  colValue: 'Valor',
  rankedBy: (indicator, y, lowerIsBetter) => (
    <>Ordenado por <b>{indicator}</b> ({y}) · <b>{lowerIsBetter ? 'menor' : 'maior'}</b> é melhor</>
  ),
  rankingCaption: (n, y) => `${n} operadoras reportaram este indicador em ${y ?? '—'}`,

  search: 'Pesquisar operadora ou país',
  searchAria: 'Pesquisar na tabela',
  noRows: 'Nenhuma linha corresponde.',
  rowsOf: (first, last, total) => `${first}–${last} de ${total}`,
  prev: 'Anterior',
  next: 'Seguinte',
  page: (i, n) => `Página ${i} / ${n}`,

  utilitiesWord: 'operadoras',
  binRange: (a, b) => `${a} a ${b}`,
  distAria: 'Distribuição dos valores das operadoras',
  barsAria: (label) => `${label} por região`,
  barTip: (label, n) => `${label} · ${n} operadoras`,

  units: { hoursYear: 'h/ano', interruptions: 'int.', days: 'dias' },
};

// Arabic counts are phrased as "label: n" to avoid number-dependent plural forms.
const ar: Messages = {
  language: 'اللغة',
  primaryNav: 'التنقل الرئيسي',
  navOverview: 'نظرة عامة',
  navTrends: 'الاتجاهات',
  navBenchmarking: 'المقارنة المعيارية',
  navMap: 'خريطة إفريقيا',
  theme: 'المظهر',
  themeLight: 'فاتح',
  themeDark: 'داكن',
  themeSystem: 'النظام',
  signOut: 'خروج',
  filtersButton: 'التصفية',

  noAccessTitle: 'لا يوجد وصول',
  noAccessBody: (role) => <>تم تسجيل دخول حسابك، لكنه لا يملك الدور {role}.</>,
  unavailableTitle: 'تسجيل الدخول غير متاح',
  unavailableBody: 'تعذّر الوصول إلى مزوّد الهوية. حاول مرة أخرى بعد قليل.',
  retry: 'إعادة المحاولة',

  noData: 'لا توجد بيانات لعوامل التصفية الحالية.',
  requestFailed: 'فشل الطلب',

  flRegions: 'المناطق',
  flIncome: 'الدخل',
  flOwnership: 'الملكية',
  none: 'لا شيء',
  all: 'الكل',

  filters: 'عوامل التصفية',
  reset: 'إعادة تعيين',
  indicator: 'المؤشر',
  country: 'البلد',
  year: 'السنة',
  yearRange: 'نطاق السنوات',
  region: 'المنطقة',
  incomeGroup: 'فئة الدخل',
  ownership: 'الملكية',
  mockNote: 'بيانات تجريبية · القيم توضيحية فقط.',
  rangeFrom: 'البداية',
  rangeTo: 'النهاية',
  rangeStart: (label) => `${label}: البداية`,
  rangeEnd: (label) => `${label}: النهاية`,

  overviewTitle: <>نظرة عامة <em>على الأداء</em></>,
  kpiUtilities: 'المرافق',
  kpiCountries: 'البلدان',
  kpiMedian: 'القيمة الوسيطة',
  kpiObservations: 'المشاهدات',
  inSelection: 'ضمن التحديد',
  noRefYear: 'لا توجد سنة مرجعية',
  utilityYears: 'سنوات المرافق',
  refYear: (y) => `السنة المرجعية ${y}`,
  medianByRegion: <>الوسيط حسب <em>المنطقة</em></>,
  distribution: <em>التوزيع</em>,
  outliers: (n) => `قيم متطرفة خارج النطاق المعروض: ${n}`,
  nUtilities: (n) => `عدد المرافق: ${n}`,
  medianIn: (y) => `الوسيط (${y ?? '—'})`,

  trendsTitle: <>الأداء <em>عبر الزمن</em></>,
  trendNote: 'منطقة واحدة مميّزة؛ مرّر المؤشر أو انقر على عنصر في وسيلة الإيضاح للتبديل',
  clickToPin: 'انقر للتثبيت',
  regions: 'المناطق',
  trendAria: 'القيمة الوسيطة للمؤشر عبر الزمن حسب المنطقة',

  benchTitle: <>المقارنة المعيارية <em>للمرافق</em></>,
  mapTitle: <>خريطة <em>إفريقيا</em></>,
  mapModeExplorer: 'المستكشف',
  mapModeCompare: 'مقارنة البلدان',
  mapModeProfile: 'ملف البلد',
  compareMaxLimit: 'يمكن مقارنة ما يصل إلى 4 بلدان في وقت واحد',
  compareSelectPrompt: 'انقر على البلدان في الخريطة لإضافتها إلى المقارنة (حتى 4 بلدان).',
  compareNoSelection: 'لم يتم تحديد أي بلد بعد',
  compareAddCountry: 'إضافة بلد',
  compareMediansVsAfrica: 'القيم الوسيطة الوطنية مقابل الوسيط الإفريقي',
  africaBenchmark: 'الوسيط الإفريقي',
  betterThanBenchmark: 'أعلى من الوسيط الإفريقي',
  worseThanBenchmark: 'أقل من الوسيط الإفريقي',
  badgeBetter: 'أفضل',
  badgeBelow: 'أدنى',
  filtersActive: 'الفلاتر نشطة',
  mapAria: 'خريطة إفريقيا',
  selectedInComparison: ' [محدد للمقارنة]',
  radarAria: 'مخطط راداري متعدد الأبعاد',
  clearAll: 'مسح الكل',
  removeCountry: (c: string) => `إزالة ${c}`,
  utilityBreakdown: 'تفصيل أداء المرافق',
  nationalMedian: 'الوسيط الوطني',
  spread: 'المدى (Δ)',
  oneObservation: 'ملاحظة واحدة',
  noDataCard: 'لا توجد بيانات',
  noObservationsForYear: 'لا توجد بيانات مرافق لهذا المؤشر في السنة المحددة.',
  memberUtilities: 'المرافق الأعضاء',
  noReportingUtilities: 'لا توجد مرافق مبلّغة',
  radarAfrMedian: 'الوسيط الإفريقي (P50)',
  categories: 'الفئات',
  components: 'المكونات',
  formula: 'الصيغة',
  score: 'الدرجة',
  radarDiagTitle: <>تشخيص <em>الأداء متعدد الأبعاد</em></>,
  radarDiagSubtitle: 'مخطط راداري متعدد المؤشرات موحّد بالنسبة للحدود القارية (مرّر فوق النقاط للتفاصيل)',
  radarFooterNote: (
    <>الحد الخارجي = <span className="text-gold font-semibold">درجة الحد الأقصى 100</span> (المؤشرات العكسية موحدة) · مرّر فوق النقاط للتعريفات والصيغ</>
  ),
  profileSelectPrompt: 'انقر على بلد في الخريطة لعرض ملف المؤشرات المتعددة الخاص به.',
  profileNoCountry: 'حدد بلداً',
  profileTitle: (country) => <>ملف البلد: <em>{country}</em></>,
  profileRadarSubtitle: 'تشخيص متعدد المؤشرات موحّد بالنسبة للحدود القارية',
  profileIndicatorsTable: 'تفصيل المؤشرات',
  profileExcellenceScore: 'درجة التميّز',
  profileAfricaMedian: 'الوسيط الإفريقي',
  profileRawValue: 'القيمة الفعلية',
  mapWorse: (inverted) => (inverted ? 'الأعلى (أسوأ)' : 'الأدنى (أسوأ)'),
  mapBetter: (inverted) => (inverted ? 'الأدنى (أفضل)' : 'الأعلى (أفضل)'),
  mapProjectionNote: 'إسقاط إيكوال إيرث (Equal Earth) · معيار حديث متساوي المساحات يحافظ على أبعاد ومساحة إفريقيا الحقيقية دون تشويه.',
  colRank: 'الترتيب',
  colUtility: 'المرفق',
  colCountry: 'البلد',
  colRegion: 'المنطقة',
  colOwnership: 'الملكية',
  colType: 'النوع',
  colValue: 'القيمة',
  rankedBy: (indicator, y, lowerIsBetter) => (
    <>مرتّبة حسب <b>{indicator}</b> ({y}) · القيمة <b>{lowerIsBetter ? 'الأدنى' : 'الأعلى'}</b> أفضل</>
  ),
  rankingCaption: (n, y) => `عدد المرافق التي أبلغت عن هذا المؤشر في ${y ?? '—'}: ${n}`,

  search: 'ابحث عن مرفق أو بلد',
  searchAria: 'البحث في الجدول',
  noRows: 'لا توجد صفوف مطابقة.',
  rowsOf: (first, last, total) => `${first}–${last} من ${total}`,
  prev: 'السابق',
  next: 'التالي',
  page: (i, n) => `الصفحة ${i} / ${n}`,

  utilitiesWord: 'مرافق',
  binRange: (a, b) => `من ${a} إلى ${b}`,
  distAria: 'توزيع قيم المرافق',
  barsAria: (label) => `${label} حسب المنطقة`,
  barTip: (label, n) => `${label} · عدد المرافق: ${n}`,

  units: { hoursYear: 'ساعة/سنة', interruptions: 'انقطاع', days: 'يوم' },
};

export const messages = { en, fr, pt, ar };
